import * as React from "react";
import { useNavigate } from "react-router-dom";
import { AuthState, AuthAction, AuthContextType } from "../types/auth";
import { notify } from "../utils/toast";
import { storage } from "../lib/storage";
import { walletManager } from "../services/wallet/walletConnection";
import { config } from "../config";
import { useWalletConnect } from "../hooks/useWalletConnect";


const initialState: AuthState = {
  user: null,
  loading: true, // Changed to true since we're checking for existing session
  error: null,
};


const AuthContext = React.createContext<AuthContextType | null>(null);

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "SET_USER":
      return { ...state, user: action.payload, loading: false, error: null };
    case "REMOVE_USER":
      return { ...state, user: null, loading: false, error: null };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };
    case "CLEAR_ERROR":
      return { ...state, error: null };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [state, dispatch] = React.useReducer(authReducer, initialState);
  const { disconnectWallet } = useWalletConnect();

  const handleReconnectWallet = React.useCallback(async () => {
    try {
      // Check if we have an active account from the wallet manager
      const activeAccount = walletManager.activeAccount;

      if (!activeAccount || !activeAccount.address) {
        // Try to resume sessions from the wallet manager
        await walletManager.resumeSessions();

        // Check again after resuming sessions
        const resumedAccount = walletManager.activeAccount;
        if (!resumedAccount || !resumedAccount.address) {
          // Fallback: use cached wallet info when manager has no active account (e.g., extension hash routes)
          const cachedAddress = localStorage.getItem("walletAddress");
          const cachedWalletType = localStorage.getItem("walletType") || "unknown";

          if (!cachedAddress) {
            dispatch({ type: "SET_LOADING", payload: false });
            return;
          }

          const profile = await storage.getProfile(cachedAddress);
          if (!profile) {
            dispatch({ type: "SET_LOADING", payload: false });
            return;
          }

          dispatch({
            type: "SET_USER",
            payload: {
              address: cachedAddress,
              walletType: cachedWalletType,
              connected: true,
            },
          });

          const currentRoute = window.location.hash || window.location.pathname;
          if (currentRoute === "/" || currentRoute === "#/" || currentRoute === "") {
            navigate("/convo", { replace: true });
          }
          return;
        }
      }

      const account = walletManager.activeAccount;
      if (account && account.address) {
        // Load profile from storage
        let profile = await storage.getProfile(account.address);

        // Check for token in localStorage
        const token = localStorage.getItem("goplausible_token");

        // If no profile but we have a token, create a profile
        if (!profile && token) {
          // Get wallet type from localStorage for backward compatibility
          const walletType = localStorage.getItem("walletType") || "unknown";

          // Create a new profile
          profile = {
            address: account.address,
            walletType: walletType,
            connected: true,
          };

          // Save the profile
          await storage.setProfile(account.address, profile);
        }

        if (!profile) {
          console.log("No profile found for address:", account.address);
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }

        // Get wallet type from localStorage for backward compatibility
        const walletType = localStorage.getItem("walletType") || "unknown";

        dispatch({
          type: "SET_USER",
          payload: {
            address: account.address,
            walletType: walletType,
            connected: true,
          },
        });

        const currentRoute = window.location.pathname;
        if (currentRoute === "/") {
          navigate("/convo", { replace: true });
        }
      } else {
        throw new Error("No accounts found");
      }
    } catch (error) {
      console.log("Reconnect error:", error);
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [navigate]);

  React.useEffect(() => {
    handleReconnectWallet();
  }, [handleReconnectWallet]);

  const signOut = React.useCallback(async () => {
      
    try {
      // Disconnect using both wallet manager and useWalletConnect
      await walletManager.disconnect();
      await disconnectWallet();

      // Clear local storage items
      localStorage.removeItem("walletconnect");
      localStorage.removeItem("walletType");
      localStorage.removeItem("walletAddress");
      localStorage.removeItem("nfdName");
      localStorage.removeItem("walletBalance");
      localStorage.removeItem("walletMinBalance");
      localStorage.removeItem("walletRekeyTo");
      localStorage.removeItem("goplausible_token");

      dispatch({ type: "REMOVE_USER" });
      notify.success("Signed out successfully");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Sign out error:", error);
      const message = "Failed to disconnect wallet";
      dispatch({ type: "SET_ERROR", payload: message });
      notify.error(message);
    }
  }, [navigate, disconnectWallet]);

  const clearError = React.useCallback(() => {
    dispatch({ type: "CLEAR_ERROR" });
  }, []);

  return React.createElement(AuthContext.Provider, {
    value: {
      state,
      signOut,
      clearError,
    },
    children,
  });
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
export const getAuthData = async () => {
    const walletAddress = localStorage.getItem("walletAddress");
    if (!walletAddress) {
      throw new Error("Wallet not connected");
    }

    const profile = await storage.getProfile(walletAddress);
    if (!profile || !profile.jwt || !profile.nonce) {
      throw new Error("Profile not found or invalid");
    }

    return {
      walletAddress,
      nonce: profile.nonce,
      jwt: profile.jwt,
      did: profile.did,
    };
  };
